import { useState, useEffect } from "react";
import { DollarSign, ShoppingCart, CreditCard } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "./avatar";
import { Card, CardContent } from "./card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./dialog";
import { Button } from "./button";

import { SharetribeRepository } from "@/infrastructure/repositories/SharetribeRepository";
import { apiService } from "@/services";

export function ProductCard({ listing, included }: any) {
  const repo = new SharetribeRepository(apiService);

  const [openBuy, setOpenBuy] = useState(false);
  const [openCheckout, setOpenCheckout] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);

  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<any>(null);
  const [_loadingPayment, setLoadingPayment] = useState(false);
  const [processingPay, setProcessingPay] = useState(false);

  const attrs = listing.attributes;

  // Product image
  const imageId = listing.relationships?.images?.data?.[0]?.id;
  const productImage =
    included.find((i: any) => i.id === imageId)?.attributes?.variants?.default
      ?.url || "/placeholder-avatar.jpg";

  // Seller
  const authorId = listing.relationships?.author?.data?.id;
  const author = included.find(
    (i: any) => i.id === authorId && i.type === "user"
  );

  const sellerName =
    author?.attributes?.profile?.displayName ||
    author?.attributes?.profile?.abbreviatedName ||
    "Unknown";

  const sellerProfilePic =
    author?.attributes?.profile?.publicData?.picture ||
    "/placeholder-avatar.jpg";

  const price = attrs.price ? attrs.price.amount / 100 : 0;

  // -----------------------------------
  // FETCH DEFAULT PAYMENT METHOD
  // -----------------------------------
  const fetchPaymentMethod = async () => {
    try {
      setLoadingPayment(true);

      const result = await repo.sharedTribecurrentUserShow(false, false, true);
      if (!result || !result.data) {
        setPaymentMethodId(null);
        return;
      }

      const includedArr = result.included || [];

      const payMethod = includedArr.find(
        (i: any) => i.type === "stripePaymentMethod"
      );

      if (payMethod) {
        setPaymentMethodId(payMethod.attributes.stripePaymentMethodId);
        setPaymentMethod(payMethod.attributes.card);
      } else {
        setPaymentMethodId(null);
      }
    } catch (err) {
      // Error is handled silently - user will see empty state
      // In production, consider logging to error tracking service
    } finally {
      setLoadingPayment(false);
    }
  };

  useEffect(() => {
    fetchPaymentMethod();
  }, []);

  // -----------------------------------
  // PAYMENT FLOW (Checkout dialog)
  // -----------------------------------

  const handlePayNow = async () => {
  if (!paymentMethodId) {
    alert("No payment method found. Please add a card.");
    return;
  }

  try {
    setProcessingPay(true);

    const qty = 1;

    const booking = {
      code: "line-item/units",
      quantity: qty,
      reversal: false,
      unitPrice: { amount: price * 100, currency: "USD" },
      lineTotal: { amount: price * 100 * qty, currency: "USD" },
      includeFor: ["customer", "provider"],
    };

    // FIX: MUST MATCH C# EXACTLY
    const providerCommission = {
      code: "line-item/provider-commission",
      percentage: -6.5,  // MUST be negative
      reversal: false,
      unitPrice: { amount: price * 100, currency: "USD" },
      lineTotal: {
        amount: Math.floor((price * 100 * qty * -6.5) / 100),
        currency: "USD",
      },
      includeFor: ["provider"],
    };

    const lineItems = [booking, providerCommission];

    // The correct process alias for your product listings
    const PROCESS_ALIAS = repo.productProcessAlias; 
    // = "flex-product-default-process/release-1"

    const res1 = await repo.transactionsInitiate(
      "transition/request-payment",
      {
        listingId: listing.id,
        paymentMethod: paymentMethodId,
        stockReservationQuantity: qty,
        lineItems,
      },
      PROCESS_ALIAS
    );

    if (!res1?.data?.id) {
      alert("Payment failed. Try again.");
      setProcessingPay(false);
      return;
    }

    const transactionId = res1.data.id;

    const res2 = await repo.transactionsTransition(
      transactionId,
      "transition/confirm-payment",
      null
    );

    if (res2?.data?.id) {
      setOpenCheckout(false);
      setOpenSuccess(true);
    } else {
      alert("Payment confirmation failed.");
    }
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
    alert(`Payment failed: ${errorMessage}`);
  }

  setProcessingPay(false);
};


  return (
    <>
      {/* =================== Product Card =================== */}
      <Card className="hover:shadow-md transition-shadow cursor-pointer">
        <CardContent className="p-6">
          <div className="flex items-start space-x-4">
            <Avatar className="h-20 w-20 rounded-lg overflow-hidden">
              <AvatarImage src={productImage} />
              <AvatarFallback>IMG</AvatarFallback>
            </Avatar>

            <div className="flex-1">
              <h3 className="font-semibold text-lg break-all [overflow-wrap:anywhere] line-clamp-2 min-w-0 max-w-full">
                {attrs.title}
              </h3>

              <p className="text-muted-foreground text-sm">
                Sold by <span className="font-medium">{sellerName}</span>
              </p>

              <div className="flex items-center space-x-2 text-lg font-semibold mt-1">
                <DollarSign className="h-5 w-5" />
                <span>{price}</span>
              </div>

              <Button
                onClick={() => setOpenBuy(true)}
                className="mt-3 flex items-center gap-2"
              >
                <ShoppingCart className="h-4 w-4" />
                Buy
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* =================== BUY DETAILS DIALOG =================== */}
      <Dialog open={openBuy} onOpenChange={setOpenBuy}>
        <DialogContent className="max-w-md w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>Buy Product</DialogTitle>
          </DialogHeader>

          <img src={productImage} className="w-full h-48 object-cover rounded-md mb-4" />

          <h2
            className="min-w-0 w-full max-w-full text-xl font-semibold whitespace-normal break-all [overflow-wrap:anywhere] leading-snug"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
            title={attrs.title}
          >
            {attrs.title}
          </h2>

          <div
            className="text-muted-foreground mt-2 pr-2"
            style={{ maxHeight: "120px", overflowY: "auto" }}
          >
            {attrs.description}
          </div>

          <div className="flex items-center gap-3 mt-4 p-3 border rounded-md">
            <Avatar>
              <AvatarImage src={sellerProfilePic} />
              <AvatarFallback>SL</AvatarFallback>
            </Avatar>

            <div>
              <p className="font-semibold">{sellerName}</p>
              <p className="text-xs text-muted-foreground">Seller</p>
            </div>
          </div>

          <div className="flex items-center space-x-2 text-xl font-bold mt-4">
            <DollarSign className="h-5 w-5 text-green-600" />
            <span>${price}</span>
          </div>

          <DialogFooter>
            <Button
              className="w-full"
              onClick={() => {
                setOpenBuy(false);
                setOpenCheckout(true);
              }}
            >
              Buy Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* =================== CHECKOUT DIALOG =================== */}
      <Dialog open={openCheckout} onOpenChange={setOpenCheckout}>
        <DialogContent className="max-w-md w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>Checkout</DialogTitle>
          </DialogHeader>

          <img
            src={productImage}
            className="w-full h-40 object-cover rounded-md mb-4 border"
          />

          <h2
            className="min-w-0 w-full max-w-full text-lg font-semibold whitespace-normal break-all [overflow-wrap:anywhere] leading-snug"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
            title={attrs.title}
          >
            {attrs.title}
          </h2>

          {/* ORDER BREAKDOWN */}
          <div className="mt-4 border rounded-md p-4">
            <p className="font-medium mb-2">Order Breakdown</p>

            <div className="flex justify-between text-sm mb-1">
              <span>Product Price</span>
              <span>${price}</span>
            </div>

            <div className="flex justify-between font-semibold text-base mt-2 border-t pt-2">
              <span>Total</span>
              <span>${price}</span>
            </div>
          </div>

          {/* PAY WITH */}
          <div className="mt-4">
            <p className="font-medium mb-2">Pay with</p>

            {!paymentMethodId ? (
              <p className="text-red-600 text-sm">No payment method found. Please add card.</p>
            ) : (
              <div className="flex items-center gap-2 border rounded-md p-3">
                <CreditCard className="h-5 w-5" />
                <span>
                  {paymentMethod?.brand?.toUpperCase()} •••• {paymentMethod?.last4}
                </span>
              </div>
            )}
          </div>

          <p className="text-xs text-muted-foreground mt-3">
            You'll be charged as soon as you tap pay button.
          </p>

          <DialogFooter>
            <Button
              className="w-full mt-2"
              disabled={processingPay || !paymentMethodId}
              onClick={handlePayNow}
            >
              {processingPay ? "Processing..." : "Confirm Pay"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* =================== SUCCESS DIALOG =================== */}
      <Dialog open={openSuccess} onOpenChange={setOpenSuccess}>
        <DialogContent className="max-w-md w-[calc(100vw-2rem)] text-center max-h-[85vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>Payment Successful</DialogTitle>
          </DialogHeader>

          <img
            src={productImage}
            className="w-full h-40 object-cover rounded-md mb-4 border"
          />

          <p
            className="min-w-0 w-full max-w-full text-lg font-semibold whitespace-normal break-all [overflow-wrap:anywhere] leading-snug"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
            title={attrs.title}
          >
            {attrs.title}
          </p>

          <p className="text-muted-foreground mt-2">
            Your purchase is complete. Enjoy your product!
          </p>

          <DialogFooter>
            <Button className="w-full" onClick={() => setOpenSuccess(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
